import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { useLocation } from 'react-router-dom';
import classnames from 'classnames';

import { UiAvatar, UiButton, UiIcon, UiNavLink } from 'shared/uikit-new';
import { COLORS, ROUTES } from 'shared/constants';
import { ApplicationModule, LayoutModule } from 'shared/modules';
import { useNavigate, useObservable, useUser } from 'shared/hooks';
import { PermissionEnum } from 'shared/enums';
import { getAdminPanelLink } from 'shared/utilities';

import { LayoutMenuMobileChatMessages } from './components/LayoutMenuMobileChatMessages';
import { LayoutNotifications } from '../LayoutNotifications';

import './styles.scss';

import logo from './assets/logo.svg';

export const LayoutMenuMobile = observer(() => {
    const navigate = useNavigate();

    const store = useObservable({
        isOpened: true,
        currentYear: new Date().getFullYear()
    });

    const location = useLocation();
    const user = useUser();

    useEffect(() => {
        store.set('isOpened', false);
        LayoutModule.aside.setOpened(false);
    }, [store, location]);

    const groups = [[{
        isVisible: ApplicationModule.user.isAvailableDashboard,
        isExternal: false,
        name: 'Дашборд',
        route: ROUTES.PROFILE_DASHBOARD(),
        icon: UiIcon.ChartNoAxesCombined,
    }, {
        isVisible: true,
        isExternal: false,
        name: 'Задачи',
        route: ROUTES.TASK_TRACKER('personal'),
        icon: UiIcon.ListTodo,
    }, {
        isVisible: ApplicationModule.user.can(PermissionEnum.WebCalendarAndReservation),
        isExternal: false,
        name: 'Календарь',
        route: ROUTES.CALENDAR(),
        icon: UiIcon.CalendarDays,
    }, {
        isVisible: ApplicationModule.user.can(PermissionEnum.WebCalendarAndReservation),
        isExternal: false,
        name: 'Переговорная',
        route: ROUTES.RESERVATIONS(),
        icon: UiIcon.MessagesSquare,
    }], [{
        isVisible: true,
        isExternal: false,
        name: 'Пропуска',
        route: ROUTES.ENTRY_PASSES(),
        icon: UiIcon.IdCard,
    },{
        isVisible: true,
        isExternal: false,
        name: 'Заявки',
        route: ROUTES.REQUESTS(),
        icon: UiIcon.NotebookPen,
    }, {
        isVisible: true,
        isExternal: false,
        name: 'Документы',
        route: ROUTES.DOCUMENTS(),
        icon: UiIcon.FileSearch,
    },], [{
        isVisible: true,
        isExternal: false,
        name: 'Компания',
        route: ROUTES.COMPANY(),
        icon: UiIcon.Network,
    }, {
        isVisible: true,
        isExternal: false,
        name: 'Сотрудники',
        route: ROUTES.COMPANY_STRUCTURE(),
        icon: UiIcon.Users,
    },], [{
        isVisible: ApplicationModule.user.can(PermissionEnum.WebCafe),
        isExternal: false,
        name: 'Столовая',
        route: ROUTES.CAFE(),
        icon: UiIcon.Soup,
    }, {
        isVisible: true,
        isExternal: false,
        name: 'База знаний',
        route: ROUTES.LIBRARY(),
        icon: UiIcon.BookMarked,
    }, {
        isVisible: true,
        isExternal: false,
        name: 'Обучение и адаптация',
        route: ROUTES.EDUCATION(),
        icon: UiIcon.Blocks,
    }, {
        isVisible: true,
        isExternal: false,
        name: 'Опросы',
        route: ROUTES.SURVEYS(),
        icon: UiIcon.MessageCircleHeart,
    }, {
        isVisible: true,
        isExternal: false,
        name: 'Вакансии',
        route: ROUTES.VACANCIES(),
        icon: UiIcon.ScanSearch,
    }, {
        isVisible: true,
        isExternal: false,
        name: 'Togas Life',
        route: ROUTES.LIFE(),
        icon: UiIcon.Radio,
    },], [{
        isVisible: ApplicationModule.user.can(PermissionEnum.AdminPanel),
        isExternal: true,
        name: 'Админ панель',
        route: getAdminPanelLink(),
        icon: UiIcon.ShieldCheck,
    },],];

    return (
        <div className='layout-menu-mobile'>
            {store.isOpened && (
                <div className='layout-menu-mobile-page'>
                    <div className='layout-menu-mobile-page__header'>
                        <UiNavLink to={ROUTES.HOME()} className='layout-menu-mobile-page__logo' style={{ backgroundImage: `url(${logo})` }}/>
                        <UiNavLink to={ROUTES.SEARCH()} className='layout-menu-mobile-page__search'>
                            <UiIcon.Search size={20} />
                        </UiNavLink>
                    </div>
                    <div className="layout-menu-mobile-page__inner">
                        <div className="layout-menu-mobile-page__user">
                            <UiAvatar image={user.imageAvatar} size={40} label={user.previewName} />
                            <span>{user.previewName}</span>
                        </div>
                        <div className="layout-menu-mobile-page__items">
                            {groups.map(group => (
                                <div key={group[0].name} className="layout-menu-mobile-page-group">
                                    {group.map(item => (
                                        <UiNavLink
                                            key={item.name}
                                            to={item.route}
                                            className={classnames('layout-menu-mobile-page-group__item',
                                                { 'layout-menu-mobile-page-group__item--active': location.pathname === item.route })}
                                        >
                                            <item.icon size={24} />
                                            <span>{item.name}</span>
                                        </UiNavLink>
                                    ))}
                                </div>
                            ))}
                        </div>
                        <div className={'layout-menu-mobile-page__copyright'}>© { store.currentYear } Togas House of Textiles.</div>
                        <div className="layout-menu-mobile__actions">
                            <UiButton
                                onClick={() => navigate(ROUTES.ENTRY_PASSES(true))}
                                style={{
                                    backgroundColor: [COLORS._EAECF1, COLORS._C6CAD1],
                                    color: [COLORS._2C2F35, COLORS._2C2F35],
                                }}
                            >
                                <UiIcon.IdCard size={20} />
                                <span>Оформить пропуск</span>
                            </UiButton>
                            <UiButton
                                onClick={() => navigate(ROUTES.RESERVATIONS())}
                                style={{
                                    backgroundColor: [COLORS._EAECF1, COLORS._C6CAD1],
                                    color: [COLORS._2C2F35, COLORS._2C2F35],
                                }}
                            >
                                <UiIcon.MessagesSquare size={20} />
                                <span>Забронировать переговорную</span>
                            </UiButton>
                        </div>
                    </div>
                </div>
            )}
            <div className="layout-menu-mobile__content">
                <UiNavLink className="layout-menu-mobile-item" to={ROUTES.HOME()}>
                    <UiIcon.House size={24} />
                </UiNavLink>
                <UiNavLink className="layout-menu-mobile-item" to={ROUTES.PROFILE()}>
                    <UiIcon.CircleUserRound size={24} />
                </UiNavLink>
                <LayoutMenuMobileChatMessages />
                <LayoutNotifications />
                <div
                    className={classnames('layout-menu-mobile-item', { 'layout-menu-mobile-item--active': LayoutModule.aside.isOpened })}
                    onClick={() => {
                        store.set('isOpened', false);
                        LayoutModule.aside.setOpened(!LayoutModule.aside.isOpened);
                    }}
                >
                    <UiIcon.LayoutDashboard size={24} />
                </div>
                <div
                    className={classnames('layout-menu-mobile-item', { 'layout-menu-mobile-item--active': store.isOpened })}
                    onClick={() => {
                        LayoutModule.aside.setOpened(false);
                        store.set('isOpened', !store.isOpened);
                    }}
                >
                    <UiIcon.Menu size={24} />
                </div>
            </div>
        </div>
    );
});
